import React from 'react';

export default function PerformanceHeatmap({ data, model }) {
  const formatDoctorName = (name) => {
    if (!name || name === 'Unknown') return 'Unknown';

    // Add a space before each capital letter for cleaner doctor names (except the first one)
    return name.replace(/([A-Z])/g, (match, letter, offset) => {
      return offset > 0 ? ` ${letter}` : letter;
    });
  };
  
  // Group performance by doctor
  const doctorPerformance = React.useMemo(() => {
    if (!data || !model) return [];
    
    const evaluations = data.stats.models[model]?.evaluations || [];
    
    const doctors = {};
    
    evaluations.forEach(val => {
      let doctor = formatDoctorName(val.doctor)
      
      if (!doctors[doctor]) {
        doctors[doctor] = {
          doctor,
          evaluations: 0,
          structureSum: 0,
          structureCount: 0,
          coverageSum: 0,
          coverageCount: 0,
          lengthSum: 0,
          lengthCount: 0,
          faithfulnessSum: 0,
          faithfulnessCount: 0
        };
      }
      
      doctors[doctor].evaluations++;
      
      // Better handling of potentially missing values
      const parseValue = (value) => {
        if (value === undefined || value === null) return null;
        const num = Number(value);
        return !isNaN(num) ? num : null;
      };
      
      const structureScore = parseValue(val.structured_score);
      const coverageScore = parseValue(val.point_coverage_score);
      const lengthScore = parseValue(val.note_length_score);
      const faithfulnessScore = parseValue(val.faithfulness_score);
      
      if (structureScore !== null) {
        doctors[doctor].structureSum += structureScore;
        doctors[doctor].structureCount++;
      }
      
      if (coverageScore !== null) {
        doctors[doctor].coverageSum += coverageScore;
        doctors[doctor].coverageCount++;
      }
      
      if (lengthScore !== null) {
        doctors[doctor].lengthSum += lengthScore;
        doctors[doctor].lengthCount++;
      }
      
      if (faithfulnessScore !== null) {
        doctors[doctor].faithfulnessSum += faithfulnessScore;
        doctors[doctor].faithfulnessCount++;
      }
    });
    
    // Calculate averages properly
    const result = Object.values(doctors).map(doc => {
      // Calculate each metric's average only if we have data
      const structure = doc.structureCount > 0 ? doc.structureSum / doc.structureCount : null;
      const coverage = doc.coverageCount > 0 ? doc.coverageSum / doc.coverageCount : null;
      const length = doc.lengthCount > 0 ? doc.lengthSum / doc.lengthCount : null;
      const faithfulness = doc.faithfulnessCount > 0 ? doc.faithfulnessSum / doc.faithfulnessCount : null;
      
      // Calculate overall score from available metrics
      let validMetrics = 0;
      let totalScore = 0;
      
      if (structure !== null) {
        totalScore += structure;
        validMetrics++;
      }
      
      if (coverage !== null) {
        totalScore += coverage;
        validMetrics++;
      }
      
      if (faithfulness !== null) {
        totalScore += faithfulness;
        validMetrics++;
      }
      
      const overallScore = validMetrics > 0 ? totalScore / validMetrics : null;
      
      return {
        doctor: doc.doctor,
        evaluations: doc.evaluations,
        structure,
        coverage,
        length,
        faithfulness,
        overallScore
      };
    }).filter(doc => doc.evaluations > 0);
    
    return result;
  }, [data, model]);

  // Sort doctors by overall performance
  const sortedDoctors = React.useMemo(() => {
    return [...doctorPerformance].sort((a, b) => 
      (b.overallScore || 0) - (a.overallScore || 0)
    );
  }, [doctorPerformance]);

  // Transform the data for the heatmap
  const heatmapData = React.useMemo(() => {
    const metrics = [
      { key: 'structure', label: 'Structure' },
      { key: 'coverage', label: 'Coverage' },
      { key: 'faithfulness', label: 'Faithfulness' },
      { key: 'length', label: 'Note Length' }
    ];
    
    const result = [];

    sortedDoctors.forEach(doctor => {
      metrics.forEach(metric => {
        const value = doctor[metric.key];
        result.push({
          x: doctor.doctor,
          y: metric.label,
          z: value !== null ? value : 0, // Convert null to 0 for visualization
          hasData: value !== null,
          count: doctor.evaluations,
          rawScore: value
        });
      });
    });

    return result;
  }, [sortedDoctors]);

  // Function to get color based on score and if data exists (can adjust)
  const getColor = (score, hasData) => {
    if (!hasData) return '#E0E0E0'; 
    if (score >= 0.8) return '#4CAF50'; 
    if (score >= 0.6) return '#8BC34A'; 
    if (score >= 0.4) return '#FFC107'; 
    if (score >= 0.2) return '#FF9800'; 
    return '#FF5722';                   
  };
  
  if (!data || !model || doctorPerformance.length === 0) {
    return (
      <div className="bg-white p-2 rounded-lg shadow text-center">
        <p className="text-gray-500">No performance data available by doctor for {model}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg h-[90%] flex flex-col">
      <div className="p-2">
        <h3 className="text-lg font-semibold mb-3 text-ktx-navy">Performance by Doctor: {model}</h3>
        <div className="text-xs text-gray-500 mb-2">
          * Doctors sorted by overall performance
        </div>
        
        <div className="mb-3 p-2 bg-gray-100 rounded text-xs">
          Found: {sortedDoctors.length} doctors, {heatmapData.length} data points
        </div>
      </div>
      
      {/* Table with sticky header */}
      <div className="flex-1 overflow-auto px-6" style={{ maxHeight: 'calc(100% - 120px)' }}>
        <div className="relative">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 border-b bg-white">Doctor</th>
                <th className="text-center p-2 border-b bg-white">Structure</th>
                <th className="text-center p-2 border-b bg-white">Coverage</th>
                <th className="text-center p-2 border-b bg-white">Faithfulness</th>
                <th className="text-center p-2 border-b bg-white">Note Length</th>
              </tr>
            </thead>
            <tbody>
              {sortedDoctors.map((doctor, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="p-2 border-b">
                    <div className="font-medium">{doctor.doctor}</div>
                    <div className="text-xs text-gray-500">{doctor.evaluations} evaluations</div>
                  </td>
                  <td className="p-2 border-b text-center">
                    <div className="inline-block w-8 h-8 rounded"
                         style={{ backgroundColor: getColor(doctor.structure, doctor.structure !== null) }}>
                    </div>
                    <div className="mt-1">{doctor.structure !== null ? doctor.structure.toFixed(2) : 'N/A'}</div>
                  </td>
                  <td className="p-2 border-b text-center">
                    <div className="inline-block w-8 h-8 rounded"
                         style={{ backgroundColor: getColor(doctor.coverage, doctor.coverage !== null) }}>
                    </div>
                    <div className="mt-1">{doctor.coverage !== null ? doctor.coverage.toFixed(2) : 'N/A'}</div>
                  </td>
                  <td className="p-2 border-b text-center">
                    <div className="inline-block w-8 h-8 rounded"
                         style={{ backgroundColor: getColor(doctor.faithfulness, doctor.faithfulness !== null) }}>
                    </div>
                    <div className="mt-1">{doctor.faithfulness !== null ? doctor.faithfulness.toFixed(2) : 'N/A'}</div>
                  </td>
                  <td className="p-2 border-b text-center">
                    <div className="inline-block w-8 h-8 rounded"
                         style={{ backgroundColor: getColor(doctor.length, doctor.length !== null) }}>
                    </div>
                    <div className="mt-1">{doctor.length !== null ? doctor.length.toFixed(2) : 'N/A'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Legend */}
      <div className="px-6 py-4 border-t mt-auto">
        <div className="flex items-center justify-center space-x-4 flex-wrap">
          <div className="text-sm mr-2">Score key:</div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4" style={{ backgroundColor: '#FF5722' }}></div>
            <span className="text-xs">Poor</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4" style={{ backgroundColor: '#FF9800' }}></div>
            <span className="text-xs">Below Average</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4" style={{ backgroundColor: '#FFC107' }}></div>
            <span className="text-xs">Average</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4" style={{ backgroundColor: '#8BC34A' }}></div>
            <span className="text-xs">Good</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4" style={{ backgroundColor: '#4CAF50' }}></div>
            <span className="text-xs">Excellent</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4" style={{ backgroundColor: '#E0E0E0' }}></div>
            <span className="text-xs">No data</span>
          </div>
        </div>
      </div>
    </div>
  );
}